// src/pages/Clinic/ClinicMembersPage.jsx
import React, { useEffect, useMemo, useState } from 'react';
import {
  Box, Paper, Typography, Button, IconButton, TextField, Select, MenuItem,
  FormControl, InputLabel, Dialog, DialogTitle, DialogContent, DialogActions,
  Grid, Chip, Tabs, Tab, Tooltip, Autocomplete, Switch, FormControlLabel,
  Table, TableHead, TableRow, TableCell, TableBody, TableContainer, Divider,
  Card, CardContent
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import {
  PersonAdd as PersonAddIcon, GroupAdd as GroupAddIcon, Edit as EditIcon,
  Delete as DeleteIcon, Settings as SettingsIcon, Send as SendIcon, ContentCopy as CopyIcon,
  Close as CloseIcon, Refresh as RefreshIcon, Search as SearchIcon, Email as EmailIcon
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import axiosInstance from '../../axiosInstance';
import { useClinic } from '../../context/ClinicContext';

const ROLES = ['Administrador', 'Médico', 'Enfermería', 'Recepción'];

// Etiquetas en español para las acciones de permisos
const PERM_LABELS = {
  view: 'Ver',
  create: 'Crear',
  edit: 'Editar',
  delete: 'Eliminar',
  export: 'Exportar',
  send: 'Enviar',
};

// Orden sugerido de flags en cada área
const PERM_ORDER = ['view', 'create', 'edit', 'delete', 'export', 'send'];

// Etiquetas en español para las áreas
const AREA_LABELS = {
  pacientes: 'Pacientes',
  citas: 'Citas',
  finanzas: 'Finanzas',
  recordatorios: 'Recordatorios',
};

const defaultPermsByRole = (rol) => ({
  pacientes: {
    view: true,
    create: rol !== 'Recepción' ? (rol !== 'Administrador' ? (rol !== 'Médico' ? false : true) : true) : true,
    edit: rol !== 'Recepción',
    delete: rol === 'Administrador',
    export: rol === 'Administrador'
  },
  citas: { view: true, create: true, edit: true, delete: rol !== 'Enfermería' },
  finanzas: { view: rol === 'Administrador' || rol === 'Médico', create: false, edit: false, delete: false },
  recordatorios: { view: true, send: rol !== 'Recepción' },
});

const prettyName = (u) => `${u.first_name || ''} ${u.last_name || ''}`.trim() || u.username || u.email;

const ClinicMembersPage = () => {
  const theme = useTheme();
  const { selectedClinicId, activeClinic } = useClinic();

  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState(0); // 0=Miembros, 1=Invitaciones
  const [members, setMembers] = useState([]);
  const [invites, setInvites] = useState([]);
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('');

  // Diálogos
  const [openInviteDoctor, setOpenInviteDoctor] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('Médico');
  const [doctorOptions, setDoctorOptions] = useState([]);
  const [selectedDoctor, setSelectedDoctor] = useState(null);

  const [openCreateStaff, setOpenCreateStaff] = useState(false);
  const [staffData, setStaffData] = useState({ first_name: '', last_name: '', email: '', phone: '', rol: 'Recepción' });

  const [openPerms, setOpenPerms] = useState(false);
  const [editingMember, setEditingMember] = useState(null);
  const [permsDraft, setPermsDraft] = useState({});

  const resendInvite = async (inv) => {
  try {
    await axiosInstance.post(`/clinics/invitations/resend/`, { token: inv.token });
    toast.success('Invitación reenviada');
  } catch (e) {
    toast.error(e?.response?.data?.error || 'No se pudo reenviar');
  }
};

  const loadData = async () => {
    if (!selectedClinicId) return;
    setLoading(true);
    try {
      const { data } = await axiosInstance.get(`/clinics/${selectedClinicId}/members/`, {
        headers: { 'X-Clinic-Id': String(selectedClinicId) }
      });
      setMembers(data.members || []);
      setInvites(data.invitations || []);
    } catch (e) {
      console.error(e);
      toast.error('No se pudieron cargar los miembros de la clínica');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadData(); /* eslint-disable-next-line */ }, [selectedClinicId]);

  const filteredMembers = useMemo(() => {
    let arr = members || [];
    if (roleFilter) arr = arr.filter(m => m.rol === roleFilter);
    if (search.trim()) {
      const s = search.trim().toLowerCase();
      arr = arr.filter(m => {
        const u = m.usuario || {};
        return (
          (u.email || '').toLowerCase().includes(s) ||
          (u.username || '').toLowerCase().includes(s) ||
          prettyName(u).toLowerCase().includes(s)
        );
      });
    }
    return arr;
  }, [members, search, roleFilter]);

  // Invitar médico (por email o seleccionando existente)
  const handleInviteDoctor = async () => {
    try {
      const email = (selectedDoctor?.email || inviteEmail || '').trim().toLowerCase();
      if (!email) return toast.info('Escribe o selecciona un email');
      const rol = 'Médico'; // fijo para invitación de médicos
      await axiosInstance.post(`/clinics/${selectedClinicId}/members/invite/`, { email, rol }, {
        headers: { 'X-Clinic-Id': String(selectedClinicId) }
      });
      toast.success('Invitación enviada');
      setOpenInviteDoctor(false);
      setInviteEmail('');
      setSelectedDoctor(null);
      loadData();
    } catch (e) {
      console.error(e);
      toast.error('No se pudo enviar la invitación');
    }
  };

  const searchDoctors = async (q) => {
    if (!q || q.trim().length < 2) return setDoctorOptions([]);
    try {
      const { data } = await axiosInstance.get(`/clinics/search/doctors/`, { params: { q } });
      setDoctorOptions(data || []);
    } catch {
      setDoctorOptions([]);
    }
  };

  // Crear staff manual
  const handleCreateStaff = async () => {
    try {
      const payload = { ...staffData };
      if (!payload.email || !payload.first_name) return toast.info('Email y nombre son requeridos');
      await axiosInstance.post(`/clinics/${selectedClinicId}/members/create-staff/`, payload, {
        headers: { 'X-Clinic-Id': String(selectedClinicId) }
      });
      toast.success('Usuario creado y notificado por email');
      setOpenCreateStaff(false);
      setStaffData({ first_name: '', last_name: '', email: '', phone: '', rol: 'Recepción' });
      loadData();
    } catch (e) {
      console.error(e);
      toast.error('No se pudo crear el usuario');
    }
  };

  const toggleActive = async (m) => {
    try {
      await axiosInstance.patch(`/clinics/${selectedClinicId}/members/${m.id}/`, { activo: !m.activo }, {
        headers: { 'X-Clinic-Id': String(selectedClinicId) }
      });
      loadData();
    } catch {
      toast.error('No se pudo cambiar el estado');
    }
  };

  const changeRole = async (m, newRole) => {
    try {
      await axiosInstance.patch(`/clinics/${selectedClinicId}/members/${m.id}/`, { rol: newRole, reset_permisos: true }, {
        headers: { 'X-Clinic-Id': String(selectedClinicId) }
      });
      toast.success('Rol actualizado');
      loadData();
    } catch {
      toast.error('No se pudo actualizar el rol');
    }
  };

  const openPermsDialog = (m) => {
    setEditingMember(m);
    setPermsDraft(m.permisos || defaultPermsByRole(m.rol));
    setOpenPerms(true);
  };

  const savePerms = async () => {
    try {
      await axiosInstance.patch(`/clinics/${selectedClinicId}/members/${editingMember.id}/`, { permisos: permsDraft }, {
        headers: { 'X-Clinic-Id': String(selectedClinicId) }
      });
      toast.success('Permisos guardados');
      setOpenPerms(false);
      setEditingMember(null);
      loadData();
    } catch {
      toast.error('No se pudieron guardar los permisos');
    }
  };

  const removeMember = async (m) => {
    if (!window.confirm(`¿Eliminar acceso de ${prettyName(m.usuario)}?`)) return;
    try {
      await axiosInstance.delete(`/clinics/${selectedClinicId}/members/${m.id}/`, {
        headers: { 'X-Clinic-Id': String(selectedClinicId) }
      });
      toast.success('Miembro eliminado');
      loadData();
    } catch {
      toast.error('No se pudo eliminar');
    }
  };

  const copyInviteLink = (inv) => {
    const url = `${window.location.origin}/clinic/join/${inv.token}`;
    navigator.clipboard.writeText(url);
    toast.success('Link copiado');
  };

  const declineInvite = async (inv) => {
    if (!window.confirm(`¿Marcar invitación a ${inv.email} como rechazada?`)) return;
    try {
      await axiosInstance.post(`/clinics/invitations/decline/`, { token: inv.token });
      toast.success('Invitación rechazada');
      loadData();
    } catch {
      toast.error('No se pudo cambiar el estado');
    }
  };

  return (
    <Box sx={{ px: { xs: 2, sm: 3, md: 4 }, py: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography variant="h3" sx={{ fontWeight: 'bold' }}>Miembros de la Clínica</Typography>
          <Chip label={activeClinic?.clinica?.nombre || ''} color="primary" variant="soft" />
        </Box>
        <Box sx={{ display: 'flex', gap: 1.5 }}>
          <Button
            variant="outlined"
            startIcon={<GroupAddIcon />}
            onClick={() => setOpenCreateStaff(true)}
            sx={{ borderRadius: '20px' }}
          >
            Crear Staff
          </Button>
          <Button
            variant="contained"
            startIcon={<PersonAddIcon />}
            onClick={() => setOpenInviteDoctor(true)}
            sx={{ borderRadius: '20px' }}
          >
            Invitar Médico
          </Button>
          <IconButton onClick={loadData} title="Refrescar"><RefreshIcon /></IconButton>
        </Box>
      </Box>

      {/* Filtros */}
      <Box sx={{ mb: 2, display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
        <Tabs value={tab} onChange={(e, v) => setTab(v)}>
          <Tab label={`Miembros (${members.length})`} />
          <Tab label={`Invitaciones (${invites.length})`} />
        </Tabs>
        {tab === 0 && (
          <>
            <Box sx={{ flex: 1 }} />
            <TextField
              size="small"
              placeholder="Buscar nombre o email"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              InputProps={{ startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} /> }}
            />
            <FormControl size="small" sx={{ minWidth: 180 }}>
              <InputLabel>Rol</InputLabel>
              <Select label="Rol" value={roleFilter} onChange={(e) => setRoleFilter(e.target.value)}>
                <MenuItem value="">Todos</MenuItem>
                {ROLES.map(r => <MenuItem key={r} value={r}>{r}</MenuItem>)}
              </Select>
            </FormControl>
          </>
        )}
      </Box>

      {/* Contenido */}
      {tab === 0 ? (
        <Paper sx={{ overflow: 'hidden', borderRadius: 3, boxShadow: 2 }}>
          <TableContainer sx={{ maxHeight: 'calc(100vh - 360px)' }}>
            <Table stickyHeader size="medium" sx={{
              '& .MuiTableCell-root': { fontSize: '1.02rem' }, '& .MuiTableCell-head': { fontWeight: 'bold' }
            }}>
              <TableHead>
                <TableRow>
                  <TableCell>Nombre</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Rol</TableCell>
                  <TableCell>Estado</TableCell>
                  <TableCell>Permisos</TableCell>
                  <TableCell align="right">Acciones</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredMembers.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                      No hay miembros
                    </TableCell>
                  </TableRow>
                ) : filteredMembers.map(m => (
                  <TableRow key={m.id} hover>
                    <TableCell>{prettyName(m.usuario)}</TableCell>
                    <TableCell>{m.usuario?.email}</TableCell>
                    <TableCell>
                      <FormControl size="small" sx={{ minWidth: 160 }}>
                        <Select value={m.rol} onChange={(e) => changeRole(m, e.target.value)}>
                          {ROLES.map(r => <MenuItem key={r} value={r}>{r}</MenuItem>)}
                        </Select>
                      </FormControl>
                    </TableCell>
                    <TableCell>
                      <FormControlLabel
                        control={<Switch checked={m.activo} onChange={() => toggleActive(m)} />}
                        label={m.activo ? 'Activo' : 'Inactivo'}
                      />
                    </TableCell>
                    <TableCell>
                      <Button size="small" startIcon={<SettingsIcon />} onClick={() => openPermsDialog(m)}>
                        Permisos
                      </Button>
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title="Eliminar acceso">
                        <IconButton onClick={() => removeMember(m)}><DeleteIcon color="error" /></IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      ) : (
        <Paper sx={{ p: 2.5, borderRadius: 3, boxShadow: 2 }}>
          <Typography variant="h6" sx={{ mb: 1.5, fontWeight: 'bold' }}>Invitaciones pendientes</Typography>
          {invites.length === 0 ? (
            <Typography color="text.secondary">No hay invitaciones pendientes</Typography>
          ) : (
            <Grid container spacing={2}>
              {invites.map(inv => (
                <Grid item xs={12} md={6} lg={4} key={inv.id}>
                  <Card sx={{ borderRadius: 3, boxShadow: 2 }}>
                    <CardContent>
                      <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>{inv.email}</Typography>
                      <Typography variant="body2" color="text.secondary">Rol: {inv.rol}</Typography>
                      <Typography variant="body2" color="text.secondary">Expira: {new Date(inv.fecha_expiracion).toLocaleString()}</Typography>
                      <Box sx={{ mt: 1.5, display: 'flex', gap: 1 }}>
                        <Button size="small" variant="outlined" startIcon={<CopyIcon />} onClick={() => copyInviteLink(inv)}>Copiar enlace</Button>
                        <Button size="small" variant="contained" startIcon={<EmailIcon />} onClick={() => resendInvite(inv)}>Reenviar</Button>
                        <Button size="small" variant="outlined" color="error" onClick={() => declineInvite(inv)}>Marcar rechazada</Button>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          )}
        </Paper>
      )}

      {/* Dialog: Invitar Médico */}
      <Dialog open={openInviteDoctor} onClose={() => setOpenInviteDoctor(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Invitar Médico</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Invita por correo o busca un médico ya registrado y envíale la invitación.
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Autocomplete
                options={doctorOptions}
                getOptionLabel={(opt) => `${prettyName(opt)} · ${opt.email}`}
                value={selectedDoctor}
                onChange={(e, v) => { setSelectedDoctor(v); setInviteEmail(v?.email || ''); }}
                onInputChange={(e, v) => searchDoctors(v)}
                renderInput={(params) => <TextField {...params} label="Buscar médico (nombre o email)" />}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField label="Correo" fullWidth value={inviteEmail} onChange={(e) => setInviteEmail(e.target.value)} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Rol</InputLabel>
                <Select label="Rol" value={inviteRole} onChange={(e) => setInviteRole(e.target.value)}>
                  <MenuItem value="Médico">Médico</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenInviteDoctor(false)} startIcon={<CloseIcon />}>Cancelar</Button>
          <Button variant="contained" startIcon={<EmailIcon />} onClick={handleInviteDoctor}>Enviar invitación</Button>
        </DialogActions>
      </Dialog>

      {/* Dialog: Crear Staff */}
      <Dialog open={openCreateStaff} onClose={() => setOpenCreateStaff(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Crear usuario de Staff</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField label="Nombre" fullWidth value={staffData.first_name} onChange={(e) => setStaffData(s => ({ ...s, first_name: e.target.value }))} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField label="Apellido" fullWidth value={staffData.last_name} onChange={(e) => setStaffData(s => ({ ...s, last_name: e.target.value }))} />
            </Grid>
            <Grid item xs={12}>
              <TextField label="Email" fullWidth value={staffData.email} onChange={(e) => setStaffData(s => ({ ...s, email: e.target.value }))} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField label="Teléfono" fullWidth value={staffData.phone} onChange={(e) => setStaffData(s => ({ ...s, phone: e.target.value }))} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Rol</InputLabel>
                <Select label="Rol" value={staffData.rol} onChange={(e) => setStaffData(s => ({ ...s, rol: e.target.value }))}>
                  {ROLES.filter(r => r !== 'Médico').map(r => <MenuItem key={r} value={r}>{r}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <Typography variant="body2" color="text.secondary">
                Se enviará un correo con usuario y contraseña temporal. Al iniciar sesión, se le pedirá cambiarla.
              </Typography>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenCreateStaff(false)} startIcon={<CloseIcon />}>Cancelar</Button>
          <Button variant="contained" startIcon={<PersonAddIcon />} onClick={handleCreateStaff}>Crear</Button>
        </DialogActions>
      </Dialog>

      {/* Dialog: Permisos */}
      <Dialog open={openPerms} onClose={() => setOpenPerms(false)} maxWidth="md" fullWidth>
        <DialogTitle>Permisos de {prettyName(editingMember?.usuario || {})}</DialogTitle>
        <DialogContent dividers sx={{ pt: 2 }}>
          {Object.keys(permsDraft || {}).map(area => (
            <Box key={area} sx={{ mb: 2.5 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                {AREA_LABELS[area] || area}
              </Typography>

              <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap', mt: 1 }}>
                {(PERM_ORDER.filter(f => permsDraft?.[area]?.hasOwnProperty(f))).map(flag => (
                  <FormControlLabel
                    key={flag}
                    control={
                      <Switch
                        checked={!!permsDraft?.[area]?.[flag]}
                        onChange={(e) => setPermsDraft(prev => ({
                          ...prev,
                          [area]: { ...(prev[area] || {}), [flag]: e.target.checked }
                        }))}
                      />
                    }
                    label={PERM_LABELS[flag] || flag}
                  />
                ))}
              </Box>
              <Divider sx={{ mt: 1.5 }} />
            </Box>
          ))}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenPerms(false)} startIcon={<CloseIcon />}>Cancelar</Button>
          <Button variant="contained" startIcon={<SettingsIcon />} onClick={savePerms}>Guardar</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ClinicMembersPage;
